import { supabase } from '@/integrations/supabase/client';
import { deployments } from './env';
import { azureCircuitBreaker } from '@/lib/security/circuitBreaker';
import { validateInput, azureRequestSchema } from '@/lib/validation/schemas';

interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export type JsonAnalysisResult = Record<string, unknown>;

export interface MiniAnalysisResponse {
  result: JsonAnalysisResult;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
}

/**
 * Sanitize and repair common JSON issues from AI responses
 */
function sanitizeJsonResponse(rawText: string): string {
  let cleaned = rawText.trim();
  
  // Remove markdown code blocks
  cleaned = cleaned.replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/```\s*$/, '');
  
  // Remove leading/trailing text that's not part of JSON
  const jsonStart = cleaned.indexOf('{');
  const jsonEnd = cleaned.lastIndexOf('}');
  if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
    cleaned = cleaned.substring(jsonStart, jsonEnd + 1);
  }
  
  // Remove comments (// and /* */)
  cleaned = cleaned.replace(/\/\/.*$/gm, '');
  cleaned = cleaned.replace(/\/\*[\s\S]*?\*\//g, '');
  
  // Fix common JSON syntax errors
  cleaned = cleaned
    .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
    .replace(/([{,]\s*)(\w+):/g, '$1"$2":') // Quote unquoted keys
    .trim();
  
  return cleaned;
}

/**
 * Attempt to parse JSON with multiple strategies
 */
function robustJsonParse(rawText: string): JsonAnalysisResult {
  // Strategy 1: Direct parse
  try {
    return JSON.parse(rawText);
  } catch (e1) {
    console.warn('⚠️ [callMiniJSON] Direct JSON parse failed, trying sanitization...');
  }
  
  // Strategy 2: Sanitize and parse
  try {
    const sanitized = sanitizeJsonResponse(rawText);
    return JSON.parse(sanitized);
  } catch (e2) {
    console.warn('⚠️ [callMiniJSON] Sanitized JSON parse failed, trying substring extraction...');
  }
  
  // Strategy 3: Try to find valid JSON substring
  try {
    const jsonMatch = rawText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const sanitized = sanitizeJsonResponse(jsonMatch[0]);
      return JSON.parse(sanitized);
    }
  } catch (e3) {
    console.warn('⚠️ [callMiniJSON] Substring extraction failed');
  }
  
  // All strategies failed
  console.error('❌ [callMiniJSON] All JSON parsing strategies failed. Raw response:', rawText.substring(0, 500));
  throw new Error('Invalid JSON response from AI model - all repair strategies failed');
}

export async function callMiniJSON(
  messages: Message[], 
  maxCompletionTokens: number = 8000
): Promise<{ result: JsonAnalysisResult, usage?: any }> {
  // Input validation
  const validated = validateInput(azureRequestSchema, {
    messages,
    max_completion_tokens: maxCompletionTokens
  });

  const controller = new AbortController();
  const timeoutMs = 120000; // 120 sekunder
  const timeout = setTimeout(() => {
    console.error('❌ [callMiniJSON] TIMEOUT efter 120 sekunder - avbryter anrop');
    controller.abort();
  }, timeoutMs);

  try {
    // Execute with circuit breaker protection
    return await azureCircuitBreaker.execute(async () => {
      console.log('🔄 [callMiniJSON] Anropar edge function azure-openai-mini...', {
        deployment: deployments.mini,
        messageCount: validated.messages.length,
        maxCompletionTokens: validated.max_completion_tokens,
        timeout: `${timeoutMs / 1000}s`
      });
      
      const startTime = Date.now();
      const { data, error } = await supabase.functions.invoke('azure-openai-mini', {
        body: { 
          messages: validated.messages, 
          deployment: deployments.mini,
          max_completion_tokens: validated.max_completion_tokens
        }
        // Note: Supabase JS client v2 doesn't support AbortSignal directly
        // Timeout is handled by setTimeout + controller.abort() above
      });
    
    clearTimeout(timeout);
    const elapsedMs = Date.now() - startTime;
    
    console.log('✅ [callMiniJSON] Edge function svarade:', { 
      hasData: !!data, 
      hasError: !!error,
      dataType: typeof data,
      elapsedMs,
      dataKeys: data ? Object.keys(data) : []
    });

    if (error) {
      console.error('❌ [callMiniJSON] Edge function error:', error);
      throw new Error(`Edge function error: ${error.message}`);
    }

    if (!data) {
      throw new Error('No response data received from edge function');
    }

    if ((data as any).error) {
      console.error('❌ [callMiniJSON] Data contains error:', (data as any).error);
      throw new Error((data as any).error);
    }

    // Handle both old format (direct result) and new format (result + usage)
    if (data.result !== undefined) {
      console.log('✅ [callMiniJSON] Returning structured response (result + usage)');
      return {
        result: data.result as JsonAnalysisResult,
        usage: data.usage
      };
    } else {
      // Fallback for old format
      console.log('⚠️ [callMiniJSON] Using fallback format (direct result)');
      return {
        result: data as JsonAnalysisResult,
        usage: null
      };
    }
    });
  } catch (error) {
    clearTimeout(timeout);
    
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('❌ [callMiniJSON] Request TIMEOUT efter 120 sekunder');
      throw new Error('Azure OpenAI timeout efter 120 sekunder');
    }
    
    console.error('❌ [callMiniJSON] Unexpected error:', {
      message: error instanceof Error ? error.message : String(error),
      type: error?.constructor?.name,
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
}